import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BookOpen, Download, Filter, Search, ChevronDown, ChevronUp,
  FileSpreadsheet, FileText, RefreshCw, Plus
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const GrandLivreSection = ({ buildingId, exercice, token }) => {
  const [activeView, setActiveView] = useState('balance');
  const [balance, setBalance] = useState([]);
  const [comptes, setComptes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [filters, setFilters] = useState({
    date_debut: exercice?.date_debut || '',
    date_fin: exercice?.date_fin || '',
    type_compte: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Expanded accounts
  const [expandedAccounts, setExpandedAccounts] = useState({});

  useEffect(() => {
    if (token && buildingId) {
      fetchData();
    }
  }, [token, buildingId, exercice]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams();
      
      if (buildingId) params.append('building_id', buildingId);
      if (filters.date_debut) params.append('date_debut', filters.date_debut);
      if (filters.date_fin) params.append('date_fin', filters.date_fin);
      
      const [balanceRes, comptesRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/grand-livre/balance?${params}`, { headers }),
        axios.get(`${API}/api/grand-livre/comptes?${params}`, { headers }),
        axios.get(`${API}/api/grand-livre/stats?${params}`, { headers })
      ]);
      
      setBalance(balanceRes.data.balance || []);
      setComptes(comptesRes.data.comptes || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const fetchEcrituresForCompte = async (compte) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams({ compte });
      if (buildingId) params.append('building_id', buildingId);
      if (filters.date_debut) params.append('date_debut', filters.date_debut);
      if (filters.date_fin) params.append('date_fin', filters.date_fin);
      
      const response = await axios.get(`${API}/api/grand-livre/ecritures?${params}`, { headers });
      return response.data.ecritures || [];
    } catch (error) {
      console.error('Error fetching ecritures:', error);
      return [];
    }
  };

  const toggleAccountExpand = async (compte) => {
    if (expandedAccounts[compte]) {
      setExpandedAccounts(prev => ({ ...prev, [compte]: null }));
    } else {
      const ecritures = await fetchEcrituresForCompte(compte);
      setExpandedAccounts(prev => ({ ...prev, [compte]: ecritures }));
    }
  };

  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams({ format });
      if (buildingId) params.append('building_id', buildingId);
      if (filters.date_debut) params.append('date_debut', filters.date_debut);
      if (filters.date_fin) params.append('date_fin', filters.date_fin);
      
      const response = await axios.get(`${API}/api/grand-livre/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `grand_livre_${new Date().toISOString().split('T')[0]}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Export ${format.toUpperCase()} généré avec succès`);
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Erreur lors de l\'export');
    }
  };

  // Group accounts by type
  const groupedAccounts = comptes.reduce((acc, compte) => {
    const type = compte.type || 'Autre';
    if (!acc[type]) acc[type] = [];
    acc[type].push(compte);
    return acc;
  }, {});

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00D3A8]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="grand-livre-section">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <BookOpen className="text-[#00D3A8]" size={24} />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Grand Livre</h2>
            <p className="text-sm text-gray-500">
              {exercice ? `Exercice : ${exercice.nom}` : 'Tous les exercices'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-gray-50 transition-colors text-sm"
          >
            <Filter size={16} />
            Filtres
          </button>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw size={16} />
          </button>
          <div className="relative group">
            <button className="flex items-center gap-2 px-3 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00BFA5] transition-colors text-sm">
              <Download size={16} />
              Exporter
              <ChevronDown size={14} />
            </button>
            <div className="absolute right-0 mt-2 w-36 bg-white rounded-lg shadow-lg border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
              <button
                onClick={() => handleExport('csv')}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-left text-sm"
              >
                <FileSpreadsheet size={14} />
                CSV
              </button>
              <button
                onClick={() => handleExport('pdf')}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-left text-sm"
              >
                <FileText size={14} />
                PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-gray-50 p-4 rounded-xl border">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date début</label>
              <input
                type="date"
                value={filters.date_debut}
                onChange={(e) => setFilters({ ...filters, date_debut: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date fin</label>
              <input
                type="date"
                value={filters.date_fin}
                onChange={(e) => setFilters({ ...filters, date_fin: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type de compte</label>
              <select
                value={filters.type_compte}
                onChange={(e) => setFilters({ ...filters, type_compte: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Tous les types</option>
                <option value="Capitaux">Capitaux</option>
                <option value="Tiers">Tiers</option>
                <option value="Financiers">Financiers</option>
                <option value="Charges">Charges</option>
                <option value="Produits">Produits</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => setFilters({ date_debut: '', date_fin: '', type_compte: '' })}
              className="px-3 py-1.5 border rounded-lg hover:bg-white text-sm"
            >
              Réinitialiser
            </button>
            <button
              onClick={() => { fetchData(); setShowFilters(false); }}
              className="px-3 py-1.5 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00BFA5] text-sm"
            >
              Appliquer
            </button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-xl border">
            <p className="text-xs text-gray-500">Écritures</p>
            <p className="text-xl font-bold text-gray-900">{stats.total_ecritures}</p>
          </div>
          <div className="bg-white p-4 rounded-xl border">
            <p className="text-xs text-gray-500">Comptes utilisés</p>
            <p className="text-xl font-bold text-gray-900">{stats.comptes_utilises}</p>
          </div>
          <div className="bg-white p-4 rounded-xl border">
            <p className="text-xs text-gray-500">Total Débit</p>
            <p className="text-xl font-bold text-green-600">{formatCurrency(stats.total_debit)}</p>
          </div>
          <div className="bg-white p-4 rounded-xl border">
            <p className="text-xs text-gray-500">Total Crédit</p>
            <p className="text-xl font-bold text-blue-600">{formatCurrency(stats.total_credit)}</p>
          </div>
        </div>
      )}

      {/* View Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveView('balance')}
          className={`px-4 py-2 text-sm font-medium transition-all border-b-2 ${
            activeView === 'balance'
              ? 'border-[#00D3A8] text-[#00D3A8]'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Balance Générale
        </button>
        <button
          onClick={() => setActiveView('plan')}
          className={`px-4 py-2 text-sm font-medium transition-all border-b-2 ${
            activeView === 'plan'
              ? 'border-[#00D3A8] text-[#00D3A8]'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Plan Comptable
        </button>
      </div>

      {/* Balance Générale */}
      {activeView === 'balance' && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Compte</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Libellé</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Débit</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Crédit</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Solde</th>
                <th className="px-4 py-3 w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {balance.length > 0 ? (
                balance.map((row) => (
                  <React.Fragment key={row.compte}>
                    <tr 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => toggleAccountExpand(row.compte)}
                    >
                      <td className="px-4 py-3 font-mono text-xs">{row.compte}</td>
                      <td className="px-4 py-3">{row.libelle}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          row.type === 'Charges' ? 'bg-red-100 text-red-700' :
                          row.type === 'Produits' ? 'bg-green-100 text-green-700' :
                          row.type === 'Tiers' ? 'bg-blue-100 text-blue-700' :
                          row.type === 'Financiers' ? 'bg-purple-100 text-purple-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {row.type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{formatCurrency(row.debit)}</td>
                      <td className="px-4 py-3 text-right font-mono">{formatCurrency(row.credit)}</td>
                      <td className={`px-4 py-3 text-right font-mono font-semibold ${
                        row.solde > 0 ? 'text-green-600' : row.solde < 0 ? 'text-red-600' : ''
                      }`}>
                        {formatCurrency(row.solde)}
                      </td>
                      <td className="px-4 py-3">
                        {expandedAccounts[row.compte] ? 
                          <ChevronUp size={14} /> : 
                          <ChevronDown size={14} />
                        }
                      </td>
                    </tr>
                    {/* Expanded écritures */}
                    {expandedAccounts[row.compte] && (
                      <tr>
                        <td colSpan={7} className="bg-gray-50 px-8 py-4">
                          {expandedAccounts[row.compte].length > 0 ? (
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-gray-500">
                                  <th className="text-left py-2">Date</th>
                                  <th className="text-left py-2">Libellé</th>
                                  <th className="text-left py-2">Pièce</th>
                                  <th className="text-right py-2">Débit</th>
                                  <th className="text-right py-2">Crédit</th>
                                </tr>
                              </thead>
                              <tbody>
                                {expandedAccounts[row.compte].map((ecriture, idx) => (
                                  <tr key={idx} className="border-t border-gray-200">
                                    <td className="py-2">{ecriture.date}</td>
                                    <td className="py-2">{ecriture.libelle}</td>
                                    <td className="py-2 font-mono">{ecriture.piece_ref || '-'}</td>
                                    <td className="py-2 text-right font-mono">
                                      {ecriture.debit > 0 ? formatCurrency(ecriture.debit) : '-'}
                                    </td>
                                    <td className="py-2 text-right font-mono">
                                      {ecriture.credit > 0 ? formatCurrency(ecriture.credit) : '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <p className="text-gray-500 text-center py-2">Aucune écriture pour ce compte</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    <BookOpen className="mx-auto mb-2 opacity-30" size={40} />
                    <p>Aucune donnée comptable disponible</p>
                    <p className="text-xs mt-1">Les écritures apparaîtront ici une fois saisies</p>
                  </td>
                </tr>
              )}
            </tbody>
            {balance.length > 0 && (
              <tfoot className="bg-gray-100 font-semibold text-sm">
                <tr>
                  <td colSpan={3} className="px-4 py-3">TOTAUX</td>
                  <td className="px-4 py-3 text-right font-mono">
                    {formatCurrency(balance.reduce((sum, r) => sum + (r.debit || 0), 0))}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {formatCurrency(balance.reduce((sum, r) => sum + (r.credit || 0), 0))}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {formatCurrency(balance.reduce((sum, r) => sum + (r.solde || 0), 0))}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* Plan Comptable */}
      {activeView === 'plan' && (
        <div className="space-y-4">
          {Object.entries(groupedAccounts).map(([type, accounts]) => (
            <div key={type} className="bg-white rounded-xl border overflow-hidden">
              <div className={`px-4 py-2.5 font-semibold flex items-center gap-2 text-sm ${
                type === 'Charges' ? 'bg-red-50 text-red-700' :
                type === 'Produits' ? 'bg-green-50 text-green-700' :
                type === 'Tiers' ? 'bg-blue-50 text-blue-700' :
                type === 'Financiers' ? 'bg-purple-50 text-purple-700' :
                'bg-gray-50 text-gray-700'
              }`}>
                <span>{type}</span>
                <span className="text-xs font-normal opacity-70">({accounts.length} comptes)</span>
              </div>
              <div className="divide-y">
                {accounts.map(compte => (
                  <div key={compte.numero} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 text-sm">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">{compte.numero}</span>
                      <span>{compte.libelle}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="text-gray-400">{compte.nb_ecritures || 0} écritures</span>
                      <span className={`font-mono font-semibold ${
                        compte.solde > 0 ? 'text-green-600' : compte.solde < 0 ? 'text-red-600' : 'text-gray-400'
                      }`}>
                        {formatCurrency(compte.solde)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GrandLivreSection;
